import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReadUser,
  useBillingPackages,
  useSelectPackage,
  useDebugCheckout,
} from "../services/api";
import PageLoading from "../components/PageLoading";
import PageError from "../components/PageError";
import Page from "../components/Pages/Page";
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Stack,
  Chip,
  Checkbox,
  Divider,
  Alert,
} from "@mui/joy";
import PageHeader from "../components/PageHeader";
import { User } from "../common/types";
import { moduleRegistry } from "../modules/moduleRegistry";
import { isDevFeaturesEnabled } from "../utils/devFeatures";
import { BugReport as BugReportIcon } from "@mui/icons-material";

interface BillingPackage {
  id: string;
  tier_name: string;
  display_name: string;
  max_users: number | null;
  base_price_monthly: number;
  available_modules: Array<{
    name: string;
    display_name: string;
    price: number;
    limits: Record<string, any>;
    allocation_mode?: string;
    description?: string;
  }>;
  storage_gb_per_user: number;
  tokens_per_user_monthly: number;
  overage_storage_per_gb: number;
  overage_tokens_per_1k: number;
  description: string;
  display_order: number;
}

const SelectPackage: React.FC = () => {
  const navigate = useNavigate();
  const readUserAPI = useReadUser();
  const getBillingPackages = useBillingPackages();
  const selectPackageAPI = useSelectPackage();
  const debugCheckoutAPI = useDebugCheckout();

  const [user, setUser] = useState<User | null>(null);
  const [packages, setPackages] = useState<BillingPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<BillingPackage | null>(
    null,
  );
  const [selectedModules, setSelectedModules] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectingPackage, setSelectingPackage] = useState<boolean>(false);
  const [debugCheckingOut, setDebugCheckingOut] = useState<boolean>(false);

  useEffect(() => {
    const initPage = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user data
        const userData = await readUserAPI();
        setUser(userData);

        // Check if user is admin
        if (userData.role_in_org !== "admin") {
          setError("Only organization administrators can select packages.");
          setLoading(false);
          return;
        }

        if (!userData.organization_id) {
          setError("No organization found.");
          setLoading(false);
          return;
        }

        // Fetch available packages
        const packagesData = await getBillingPackages();
        setPackages(packagesData || []);

        setLoading(false);
      } catch (err: any) {
        console.error("Failed to initialize select package page:", err);
        setError(err.message || "Failed to load packages");
        setLoading(false);
      }
    };

    initPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePackageSelect = (pkg: BillingPackage) => {
    setSelectedPackage(pkg);
    // Pre-select all available modules by default
    const moduleNames = pkg.available_modules.map((m) => m.name);
    setSelectedModules(new Set(moduleNames));
  };

  const handleModuleToggle = (moduleName: string) => {
    setSelectedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleName)) {
        newSet.delete(moduleName);
      } else {
        newSet.add(moduleName);
      }
      return newSet;
    });
  };

  const getModuleDisplayName = (
    moduleName: string,
    fallbackName: string,
  ): string => {
    const moduleConfig = moduleRegistry[moduleName];
    return moduleConfig?.name || fallbackName;
  };

  const calculateTotal = (): number => {
    if (!selectedPackage) return 0;

    let total = selectedPackage.base_price_monthly;
    selectedPackage.available_modules.forEach((module) => {
      if (selectedModules.has(module.name)) {
        total += module.price;
      }
    });

    return total;
  };

  const handleSelectPackage = async () => {
    if (
      !selectedPackage ||
      !user?.organization_id ||
      selectedModules.size === 0
    )
      return;

    setSelectingPackage(true);
    try {
      const response = await selectPackageAPI(
        user.organization_id,
        selectedPackage.id,
        Array.from(selectedModules),
      );

      console.log("Select package response:", response);

      // Response should contain stripe_checkout_url
      if (response.stripe_checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.stripe_checkout_url;
      } else {
        console.error("No stripe_checkout_url in response:", response);
        setError(
          "No checkout URL received from server. Stripe may not be configured.",
        );
        setSelectingPackage(false);
      }
    } catch (err: any) {
      console.error("Failed to select package:", err);
      const errorMessage =
        err?.response?.detail ||
        err?.message ||
        "Failed to select package. Please try again.";
      setError(`Selection Error: ${errorMessage}`);
      setSelectingPackage(false);
    }
  };

  const handleDebugCheckout = async () => {
    if (
      !selectedPackage ||
      !user?.organization_id ||
      selectedModules.size === 0
    )
      return;

    setDebugCheckingOut(true);
    try {
      const response = await debugCheckoutAPI(
        user.organization_id,
        selectedPackage.id,
        Array.from(selectedModules),
      );

      console.log("Debug checkout response:", response);

      // Success! Redirect to dashboard to see active organization
      alert("Subscription activated successfully! (Dev Mode)");
      navigate("/");
    } catch (err: any) {
      console.error("Failed to debug checkout:", err);
      const errorMessage =
        err?.response?.detail ||
        err?.message ||
        "Failed to activate subscription. Please try again.";
      setError(`Debug Checkout Error: ${errorMessage}`);
      setDebugCheckingOut(false);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return (
      <Page>
        <PageHeader>
          <Typography level="h2">Select Package</Typography>
        </PageHeader>
        <PageError>{error}</PageError>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Select Your Plan</Typography>
      </PageHeader>

      <Alert color="primary" variant="soft" sx={{ mb: 3 }}>
        <Typography level="body-sm">
          Choose a billing package to activate your organization and start using
          MetalMind.
        </Typography>
      </Alert>

      {!selectedPackage ? (
        // Package selection view
        <Box>
          <Typography level="body-lg" sx={{ mb: 3 }}>
            Select a plan for your organization.
          </Typography>
          <Stack
            direction="row"
            sx={{
              flexWrap: "wrap",
              gap: 2,
              "& > *": {
                flex: "1 1 280px",
                minWidth: 280,
                maxWidth: 400,
              },
            }}
          >
            {packages.map((pkg) => {
              const isEnterprise = pkg.base_price_monthly === 0;
              const isContactSales = isEnterprise;

              return (
                <Card
                  key={pkg.id}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    cursor: isContactSales ? "default" : "pointer",
                    transition: "all 0.2s",
                    "&:hover": isContactSales
                      ? {}
                      : {
                          boxShadow: "md",
                          borderColor: "primary.500",
                        },
                  }}
                  onClick={
                    isContactSales ? undefined : () => handlePackageSelect(pkg)
                  }
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography level="h2" sx={{ color: "primary.500" }}>
                          {pkg.display_name}
                        </Typography>
                      </Box>
                      <Box>
                        {isContactSales ? (
                          <Typography level="title-lg">
                            Contact Sales
                          </Typography>
                        ) : (
                          <Typography level="title-lg">
                            ${pkg.base_price_monthly.toFixed(2)}
                            <Typography
                              level="body-sm"
                              component="span"
                              sx={{ ml: 0.5 }}
                            >
                              /month
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                      <Divider />
                      <Stack spacing={1}>
                        <Typography level="body-sm">
                          <strong>{pkg.max_users || "Unlimited"}</strong> users
                        </Typography>
                        {(pkg.storage_gb_per_user > 0 ||
                          pkg.storage_gb_per_user === -1) && (
                          <Typography level="body-sm">
                            <strong>
                              {pkg.storage_gb_per_user === -1
                                ? "Unlimited"
                                : `${pkg.storage_gb_per_user}GB`}
                            </strong>{" "}
                            storage per user
                          </Typography>
                        )}
                        {(pkg.tokens_per_user_monthly > 0 ||
                          pkg.tokens_per_user_monthly === -1) && (
                          <Typography level="body-sm">
                            <strong>
                              {pkg.tokens_per_user_monthly === -1
                                ? "Unlimited"
                                : pkg.tokens_per_user_monthly.toLocaleString()}
                            </strong>{" "}
                            tokens per user/month
                          </Typography>
                        )}
                      </Stack>
                      {pkg.description && (
                        <Typography
                          level="body-sm"
                          sx={{ color: "text.secondary" }}
                        >
                          {pkg.description}
                        </Typography>
                      )}
                      {isContactSales ? (
                        <Button
                          variant="solid"
                          color="primary"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = "mailto:hello@metalmind.ai";
                          }}
                        >
                          Contact Sales
                        </Button>
                      ) : (
                        <Button variant="solid" color="primary" fullWidth>
                          Select Package
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>
      ) : (
        // Module selection and confirm view
        <Box>
          <Button
            variant="plain"
            onClick={() => {
              setSelectedPackage(null);
              setSelectedModules(new Set());
            }}
            sx={{ mb: 2 }}
          >
            ← Back to Packages
          </Button>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography level="h2" sx={{ color: "primary.500" }}>
                    {selectedPackage.display_name}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography level="title-sm" sx={{ mb: 2 }}>
                    Base Package
                  </Typography>
                  <Stack spacing={1}>
                    <Typography level="body-sm">
                      <strong>
                        ${selectedPackage.base_price_monthly.toFixed(2)}/month
                      </strong>
                    </Typography>
                    <Typography level="body-sm">
                      {selectedPackage.max_users || "Unlimited"} users
                    </Typography>
                    <Typography level="body-sm">
                      {selectedPackage.storage_gb_per_user}GB storage per user
                    </Typography>
                    <Typography level="body-sm">
                      {selectedPackage.tokens_per_user_monthly.toLocaleString()}{" "}
                      tokens per user/month
                    </Typography>
                  </Stack>
                </Box>

                {selectedPackage.available_modules.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography level="title-sm" sx={{ mb: 2 }}>
                        Available Modules
                      </Typography>
                      <Stack spacing={1.5}>
                        {selectedPackage.available_modules.map((module) => (
                          <Box
                            key={module.name}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                              p: 1.5,
                              borderRadius: "sm",
                              bgcolor: "background.surface",
                            }}
                          >
                            <Checkbox
                              checked={selectedModules.has(module.name)}
                              onChange={() => handleModuleToggle(module.name)}
                              label={
                                <Box sx={{ flex: 1 }}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: "md" }}
                                    >
                                      {getModuleDisplayName(
                                        module.name,
                                        module.display_name,
                                      )}
                                    </Typography>
                                    {module.price > 0 && (
                                      <Chip
                                        size="sm"
                                        variant="soft"
                                        color="primary"
                                      >
                                        +${module.price.toFixed(2)}/month
                                      </Chip>
                                    )}
                                  </Stack>
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography level="title-lg">
                        Total Monthly Cost
                      </Typography>
                      <Typography level="h3" sx={{ color: "primary.500" }}>
                        ${calculateTotal().toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Stack spacing={2}>
                  <Button
                    variant="solid"
                    color="primary"
                    size="lg"
                    fullWidth
                    onClick={handleSelectPackage}
                    disabled={
                      selectedModules.size === 0 ||
                      selectingPackage ||
                      debugCheckingOut
                    }
                    loading={selectingPackage}
                  >
                    {selectingPackage ? "Processing..." : "Continue to Payment"}
                  </Button>

                  {isDevFeaturesEnabled() && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="lg"
                      fullWidth
                      startDecorator={<BugReportIcon />}
                      onClick={handleDebugCheckout}
                      disabled={
                        selectedModules.size === 0 ||
                        selectingPackage ||
                        debugCheckingOut
                      }
                      loading={debugCheckingOut}
                    >
                      {debugCheckingOut
                        ? "Activating..."
                        : "Debug: Skip Payment & Activate"}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </Page>
  );
};

export default SelectPackage;
